// The icon editor's logic for icon entries: which settings a user-uploaded PNG icon takes,
// the flip value of an icon's style, the entry the icon library writes for a clicked Iconify
// or uploaded icon (with its SVG's size fitted into 10 mm), the check for an animated Iconify
// icon and the print quality of an uploaded PNG at the size the user set. IconEditor held
// them until PR 23 of the test backfill moved them here; RenderKeycapIcons reads normalizeFlip too.
// normalizeIconObject, the fields version 1.1 added to an entry, came from customizerCompat.js in
// PR 25: it is pure, and customizerCompat.js opens IndexedDB on import.
//
// An icon entry is a value of a keycap's `icons` (see iconEntryDefaults.js): `source`
// 'iconify' or 'user_asset', `format` 'png' or 'svg' for an uploaded icon and null for an
// Iconify one, its `style` (width and height in mm, fill, stroke, flip) and `position`.

const MAX_ICON_SIZE_MM = 10; // a new icon is scaled down so that its larger dimension is this
const PNG_PRINT_DPI = 720; // the dpi the printing files render PNG icons at (svg -> pdf): an uploaded PNG starts at the size that prints it at this dpi, and the quality badge rates the size the user set against it
const MM_PER_INCH = 25.4;

const PNG_INCOMPATIBLE_ICON_UPDATE_PATHS = new Set([
  'style.fill',
  'style.stroke',
  'style.strokeWidth',
  'isActiveStates.fill',
  'isActiveStates.stroke',
  'selectedIconColorIndexes.fill',
  'selectedIconColorIndexes.stroke',
]); // settings that should not be applied to PNG icons

export const normalizeIconObject = (iconObj) => ({
  ...iconObj,
  source: iconObj.source || 'iconify', // default to 'iconify', because design documents/states previous to customizerVersion 1.1 only used iconify icons
  assetId: iconObj.assetId ?? null,
  format: iconObj.format ?? null,
}); // helper function to safely read and update icon objects, based on the customizerVersion 1.1 feature update (user uploaded icons)

export const isPngIconEntry = (iconObj) =>
  iconObj.source === 'user_asset' && iconObj.format === 'png'; // checks if an icon entry is a user-uploaded PNG (an entry without `source` or `format`, which the design migration and the icon library's clicks set, fails the comparison exactly as normalizeIconObject's defaults, 'iconify' and null, did; no writer leaves a hole in `icons`, so the old default parameter went with the normalising)

export const supportsSvgIconSettings = (iconObj) => !isPngIconEntry(iconObj); // checks if an icon entry supports SVG-style settings like fill, stroke and compact boundingBox

export const adaptIconUpdates = (updates, iconObj) => {
  if (!isPngIconEntry(iconObj)) {
    return updates; // an SVG icon takes every setting, and a switch to a PNG carries the resets the upload click makes
  }

  if (!isPngIconEntry({ ...iconObj, ...updates })) {
    return 'position.boundingBox' in updates
      ? updates
      : { ...updates, 'position.boundingBox': 'compact' }; // the switch out of the PNG format, applied whole: the upload click sets an SVG upload's compact bounding box itself, and a library icon, whose click sets none, gets a new entry's compact box in place of the standard one a PNG is held to (per entry, as a click sets every selected entry; a library icon chosen over another SVG icon keeps the user's box: user decision, Oct 2026). Judged by the old entry alone, as before PR 23, the switch kept the standard box
  }

  return Object.entries(updates).reduce((acc, [path, value]) => {
    if (PNG_INCOMPATIBLE_ICON_UPDATE_PATHS.has(path)) {
      return acc; // block settings that do not apply to PNG icons
    }

    if (path === 'position.boundingBox' && value !== 'standard') {
      return acc; // PNG icons only support the standard bounding box
    }

    acc[path] = value;

    return acc;
  }, {});
}; // the updates an icon entry takes: a PNG icon's SVG-only settings (fill, stroke, colour-list references) and a bounding box other than standard dropped, the compact box restored when it stops being a PNG

export function normalizeFlip(v) {
  if (!v || v === 'none') return 'none'; // if the current flip value is 'none', just return it

  const parts = String(v).split(' ').filter(Boolean); // get all words out of the current flip value ('vertical' and/or 'horizontal')
  const set = new Set(parts); // de-duplicate (for safety)
  const hasH = set.has('horizontal'); // check if 'horizontal' is part of the current flip value
  const hasV = set.has('vertical'); // check if 'vertical' is part of the current flip value

  // return the normalized flip values:
  if (hasH && hasV) return 'horizontal vertical';
  if (hasH) return 'horizontal';
  if (hasV) return 'vertical';

  return 'none';
} // helper function to normalize the current flip value to one of the allowed strings ('none', 'horizontal', 'vertical' or 'horizontal vertical')

export function toggleFlipValue(currentValue, direction) {
  const cur = normalizeFlip(currentValue); // normalize the current flip value
  if (cur === 'none') return direction; // if the icon is not fliped yet, just return the clicked flip value ('horizontal' or 'vertical')

  const parts = new Set(cur.split(' ')); // get all words out of the current flip value ('vertical' and/or 'horizontal')
  if (parts.has(direction)) {
    parts.delete(direction); // if the current flip value already includes the clicked flip value -> remove it
  } else {
    parts.add(direction); // if the clicked flip value is not present in the current flip value -> add it
  }

  if (parts.size === 0) return 'none'; // if no flip values are left -> return 'none'

  // keep stable order (horizontal first, then vertical):
  const out = [];
  if (parts.has('horizontal')) out.push('horizontal'); // add 'horizontal' to the out array, if it is part of the new flip value
  if (parts.has('vertical')) out.push('vertical'); // add 'vertical' to the out array, if it is part of the new flip value

  return out.join(' '); // return the final flip value
} // helper function to toggle a single flip value ('horizontal' or 'vertical')

export const parseSvgDimensions = (svgText) => {
  const viewBox = new DOMParser()
    .parseFromString(svgText, 'image/svg+xml')
    .documentElement.getAttribute('viewBox'); // the icon's own coordinate size, which every Iconify icon carries; its width and height attributes are that ratio in em, rounded to two decimals, so the viewBox is read instead

  if (viewBox) {
    const [, , width, height] = viewBox
      .trim()
      .split(/[\s,]+/)
      .map(Number); // minX minY width height, separated by whitespace or commas
    return {
      width: (MAX_ICON_SIZE_MM * width) / height,
      height: MAX_ICON_SIZE_MM,
    }; // at a height of 10 mm, as Iconify's height="1em" sized every icon before PR 23 (1em as 10), with the width from the exact ratio (a malformed viewBox, a height of 0 or a value missing, gives a non-finite width: Iconify's viewBoxes are four positive numbers, and the em attributes, read first before PR 23, would have shielded the division)
  }

  return { width: MAX_ICON_SIZE_MM, height: MAX_ICON_SIZE_MM }; // square if no viewBox is specified (not an Iconify form)
}; // the size an SVG icon starts with before the fit: 10 mm high and as wide as its viewBox's ratio makes it (fitIconSize scales a wider one down to 10 mm wide); read from the rounded em sizes before PR 23, fa6-solid:house started at 10 x 8.85 mm instead of 10 x 8.89

export const fitIconSize = ({ width, height }) => {
  // Scaling dimensions so that the largest does not exceed 10:
  const maxDimension = Math.max(width, height);
  const scale =
    maxDimension > MAX_ICON_SIZE_MM ? MAX_ICON_SIZE_MM / maxDimension : 1; // Calculate scale factor if needed

  const fitted = (dimension) =>
    scale !== 1 && dimension === maxDimension
      ? MAX_ICON_SIZE_MM
      : dimension * scale; // the larger dimension exactly 10: its product with the scale can come out as 9.999999999999998, which the size field then showed

  return {
    width: fitted(width), // Apply scaling to width
    height: fitted(height), // Apply scaling to height
    aspectRatio: width / height, // of the original, for style.originalAspectRatio
  };
}; // the size a new icon starts with: scaled down so that its larger dimension is 10 mm, a smaller icon kept as it is

export const pngPxToMm = (px) => (px / PNG_PRINT_DPI) * MM_PER_INCH; // helper function to convert the width and height of a PNG from px to mm, based on a dpi of 720 (PNG_PRINT_DPI)

const iconifySvgUrl = (icon) =>
  `https://api.iconify.design/${icon.replace(':', '/')}.svg`; // the API's SVG endpoint, prefix/name.svg: the prefix:name form, which the search results name icons by, answers 404 (Oct 2026), although the API's published source still lists that route

export const fetchIconifySvg = async (icon) => {
  const response = await fetch(iconifySvgUrl(icon));
  if (!response.ok) {
    throw new Error(`Failed to fetch SVG: ${response.status}`);
  }
  return response.text();
}; // the SVG of a library icon ('prefix:name'), downloaded once per click for the animation check and the size

export const iconifyIconUpdates = ({ icon, collection, size }) => {
  const { width, height, aspectRatio } = fitIconSize(size);

  return {
    source: 'iconify',
    icon,
    iconCollection: {
      name: collection?.name,
      author: {
        name: collection?.author?.name,
        url: collection?.author?.url,
      },
      license: {
        spdx: collection?.license?.spdx,
        title: collection?.license?.title,
        url: collection?.license?.url,
      },
    }, // Safely extract only specified fields from author and license
    assetId: null,
    format: null,
    'style.originalAspectRatio': aspectRatio,
    'style.width': width,
    'style.height': height,
  };
}; // the change the icon library writes over the selected entries when an Iconify icon is clicked: the icon's name ('prefix:name'), its collection's entry of Iconify's collections list ({} when the list lacks it) and its SVG's size (parseSvgDimensions), scaled so that the larger dimension is 10 mm

export const uploadedIconUpdates = (userIcon) => {
  const { width, height, aspectRatio } = fitIconSize(
    userIcon.format === 'png'
      ? {
          width: pngPxToMm(userIcon.widthPx),
          height: pngPxToMm(userIcon.heightPx),
        }
      : {
          width: userIcon.viewBox.width,
          height: userIcon.viewBox.height,
        }
  ); // get the icons width and height (a PNG's at 720 dpi, an SVG's viewBox; the icon model requires both to be at least 1), scaled so that the larger dimension is 10 mm

  return {
    source: 'user_asset',
    icon: null,
    iconCollection: null,
    assetId: userIcon._id,
    format: userIcon.format,
    'style.originalAspectRatio': aspectRatio,
    'style.width': width,
    'style.height': height,
    ...(userIcon.format === 'png'
      ? {
          'isActiveStates.fill': false,
          'isActiveStates.stroke': false,
          'selectedIconColorIndexes.fill': null,
          'selectedIconColorIndexes.stroke': null,
          'position.boundingBox': 'standard',
        }
      : {
          'position.boundingBox': 'compact',
        }), // reset/deactivate setting for PNG icons, that are only available to SVG icons
  };
}; // the change the icon library writes over the selected entries when an uploaded icon is clicked (as GET /api/user-icons/previews lists it: _id, format, widthPx and heightPx for a PNG, viewBox for an SVG)

export function isAnimatedSVG(svgString) {
  // 1) Parse the SVG into a DOM object
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');

  // 2) Check for SMIL animation elements, a <script> or an inline CSS animation (any style attribute naming an animation, over-inclusive by design: Iconify writes none)
  if (
    doc.querySelector(
      'animate, animateTransform, animateMotion, animateColor, set, script, [style*="animation"]'
    )
  ) {
    return true;
  }

  // 3) Check for CSS-based animations in <style> elements (e.g., look for 'animation:' or '@keyframes' in the text content)
  const styleElements = doc.querySelectorAll('style');
  for (const styleEl of styleElements) {
    const styleContent = styleEl.textContent;
    if (
      /animation\s*:/i.test(styleContent) ||
      /@keyframes/i.test(styleContent)
    ) {
      return true;
    }
  }

  return false; // No animation markers found
} // whether an Iconify icon's SVG is animated (the customizer prints icons, so it refuses them): any SMIL animation element (Iconify's animated sets use animate, animateTransform and set), a script, or a CSS animation in a style element or attribute

const parseMmValue = (value) => {
  const parsed = Number.parseFloat(value); // the stored width or height (a number) or the mm input's value as typed (a number input's string: digits, '0' or '-5'; the editor shows the stored size for an empty field)

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}; // the mm value of a size field as a positive number, or null for 0 or a negative value (neither source yields NaN, so the finite check is a guard alone)

export const calculatePngDpiQuality = ({
  widthPx,
  heightPx,
  widthMm,
  heightMm,
}) => {
  const safeWidthMm = parseMmValue(widthMm);
  const safeHeightMm = parseMmValue(heightMm);

  if (!safeWidthMm || !safeHeightMm) {
    return null; // no badge while a size field holds 0 or a negative number (widthPx and heightPx are the uploaded PNG's: the upload reads them from the image and refuses a PNG without a size, and the icon model checks at least 1)
  }

  const widthInches = safeWidthMm / MM_PER_INCH; // convert the png width, set by the user, to inches
  const heightInches = safeHeightMm / MM_PER_INCH; // convert the png height, set by the user,to inches

  const dpiX = widthPx / widthInches; // calculate the dpi for the width of the png, based on the width in inches (set by the user) and the width in px of the original png file uploaded by the user
  const dpiY = heightPx / heightInches; // calculate the dpi for the height of the png, based on the height in inches (set by the user) and the height in px of the original png file uploaded by the user
  const dpi = Math.floor(Math.min(dpiX, dpiY)); // use the lower dpi, because stretched PNGs may have different horizontal/vertical quality

  // the label is the badge's tooltip, in English in both languages (left as it is, user decision Oct 2026):
  if (dpi >= PNG_PRINT_DPI) {
    return {
      dpi,
      displayDpi: `≥ ${PNG_PRINT_DPI}`,
      label: 'Best',
      className: 'png-dpi-badge-best',
    };
  }

  if (dpi >= 600) {
    return {
      dpi,
      displayDpi: String(dpi),
      label: 'Good',
      className: 'png-dpi-badge-good',
    };
  }

  if (dpi >= 300) {
    return {
      dpi,
      displayDpi: String(dpi),
      label: 'Medium',
      className: 'png-dpi-badge-medium',
    };
  }

  return {
    dpi,
    displayDpi: String(dpi),
    label: 'Low',
    className: 'png-dpi-badge-low',
  };
}; // calculates the effective PNG dpi and returns a quality label for the UI

export const isValidIconEntryForEditor = (iconObj) => {
  const normalizedIcon = normalizeIconObject(iconObj); // adds missing default icon fields so older saved Iconify icons and new uploaded icons can be checked with one shape

  if (normalizedIcon.source === 'user_asset') {
    return Boolean(normalizedIcon.assetId && normalizedIcon.format);
  }

  return Boolean(normalizedIcon.icon && normalizedIcon.icon.trim() !== '');
}; // check if an icon entry has enough data to be shown in editor overlays, regardless of its visibility (RenderKeycapIcons, PR 32)

export const isRenderableIcon = (iconObj) => {
  const normalizedIcon = normalizeIconObject(iconObj); // adds missing default icon fields so older saved Iconify icons and new uploaded icons can be checked with one shape

  if (normalizedIcon.visible === false) {
    return false; // hidden icons should not be rendered onto the keycap
  }

  return isValidIconEntryForEditor(normalizedIcon);
}; // check if an icon entry should be rendered onto the keycap (RenderKeycapIcons, PR 32)
